import PropTypes from 'prop-types';
import { useMemo } from 'react';

const DESIGN_WIDTH = 63;
const DESIGN_BOTTOM = 114;
/** Top of vertical bin walls (below lid / hopper mouth). */
const TOP_END = 14.5397;
/** Y where side walls meet the bottom cone. */
const CONE_START = 80.3739;
const MID_VB = CONE_START - TOP_END;
const TOP_VB = TOP_END;
const BOT_VB = DESIGN_BOTTOM - CONE_START;

export const SINGLE_BIN_ICON_HEIGHT_SCALE_MIN = 0.22;
export const SINGLE_BIN_ICON_HEIGHT_SCALE_MAX = 1;

/**
 * Display px height of body + cone (lid excluded). Cone is fixed; cylindrical section scales with capacity.
 * @param {number} heightScale
 * @returns {number}
 */
export function fillableStackHeightPx(heightScale) {
  const h = Math.min(
    SINGLE_BIN_ICON_HEIGHT_SCALE_MAX,
    Math.max(SINGLE_BIN_ICON_HEIGHT_SCALE_MIN, heightScale),
  );
  return MID_VB * h + BOT_VB;
}

const PATH_BODY =
  'M61.6484 80.3739L46.5541 112.526H16.2573L1.05469 80.3739V14.5397H61.6484V80.3739Z';
const PATH_TOP =
  'M39.9691 3.8222H22.734L1.05469 14.5276H61.6484L39.9691 3.8222Z';
const PATH_CAP =
  'M38.1861 0.926056H24.5161C23.5164 0.926056 22.7095 1.73288 22.7095 2.73237V3.86433H39.9927V2.73237C39.9927 1.73288 39.1858 0.926056 38.1861 0.926056Z';
const PATH_SEPARATOR = 'M1.5603 80.5906L61.1548 80.5906';

const PATH_LINES = [
  'M2.64429 61.239H33.2422',
  'M2.64429 66.0739H33.2422',
  'M2.64429 70.668H33.2422',
  'M2.64429 75.262H39.3425',
];

/**
 * Map distance from top of stacked icon (px) to design-space Y (same as viewBox y).
 * Piecewise linear: display height tracks silhouette bands (lid / body / cone).
 */
function displayFromTopPxToOrigY(dFromTop, topPx, midPx, botPx) {
  const d = Math.max(0, dFromTop);
  if (topPx > 0 && d <= topPx) {
    return (d / topPx) * TOP_END;
  }
  if (midPx > 0 && d <= topPx + midPx) {
    const intoMid = d - topPx;
    return TOP_END + (intoMid / midPx) * MID_VB;
  }
  if (botPx > 0) {
    const intoBot = d - topPx - midPx;
    return CONE_START + Math.min(1, Math.max(0, intoBot / botPx)) * BOT_VB;
  }
  return DESIGN_BOTTOM;
}

/**
 * Feed surface Y in design vb: fullness P% occupies P% of **body + cone** stacked height only.
 * The top band (lid / hopper mouth) is not fillable and stays empty.
 */
function fillSurfaceYFromPercent(fillPercent, topPx, midPx, botPx) {
  const fillablePx = midPx + botPx;
  if (fillablePx <= 0) return DESIGN_BOTTOM;
  const p = Math.min(100, Math.max(0, fillPercent));
  const depthFromBodyTopPx = (1 - p / 100) * fillablePx;
  const surfaceFromTopPx = topPx + depthFromBodyTopPx;
  const y = displayFromTopPxToOrigY(surfaceFromTopPx, topPx, midPx, botPx);
  return Math.min(DESIGN_BOTTOM, Math.max(TOP_END, y));
}

/**
 * Single-bin silo: lid + hopper band fixed; cone fixed height; only the cylindrical section scales
 * with `heightScale`. Multi-bin equal-mass alignment is handled by adjusted fill % in the card.
 */
export function SingleBinIcon({
  className = '',
  fillPercent = 0,
  fillColour = '',
  onClick = () => null,
  heightScale = 1,
}) {
  const uid = useMemo(() => Math.random().toString(36).slice(2, 11), []);

  const fillClipId = `${uid}-fill-interior`;
  const bandTopId = `${uid}-band-top`;
  const bandMidId = `${uid}-band-mid`;
  const bandBotId = `${uid}-band-bot`;

  const hScale = Math.min(SINGLE_BIN_ICON_HEIGHT_SCALE_MAX, Math.max(SINGLE_BIN_ICON_HEIGHT_SCALE_MIN, heightScale));

  // Lid + cone fixed height; body scales with capacity.
  const topPx = TOP_VB;
  const midPx = MID_VB * hScale;
  const botPx = BOT_VB;

  // Surface Y: fillPercent maps to body+cone only (lid band excluded).
  const fillY = fillSurfaceYFromPercent(fillPercent, topPx, midPx, botPx);

  const interiorClipPaths = (
    <>
      <path d={PATH_BODY} />
      <path d={PATH_TOP} />
      <path d={PATH_CAP} />
      <path d={PATH_SEPARATOR} />
    </>
  );

  const midFillStart = Math.max(fillY, TOP_END);
  const botFillStart = Math.max(fillY, CONE_START);

  return (
    <span
      className={className}
      onClick={onClick}
      style={{
        display: 'inline-block',
        width: `${DESIGN_WIDTH}px`,
        verticalAlign: 'bottom',
      }}
    >
      {/* Top: lid + hopper — fixed height */}
      <svg
        width={DESIGN_WIDTH}
        height={topPx}
        viewBox={`0 0 ${DESIGN_WIDTH} ${TOP_END}`}
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <defs>
          <clipPath id={bandTopId}>
            <rect x="0" y="0" width={DESIGN_WIDTH} height={TOP_END} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${bandTopId})`}>
          <path d={PATH_BODY} fill="#E6E6E6" />
          <path d={PATH_BODY} stroke="#999999" strokeMiterlimit="10" />
          <path d={PATH_TOP} fill="#F1F1F1" stroke="#999999" strokeLinecap="round" strokeLinejoin="round" />
          <path d={PATH_CAP} fill="#F1F1F1" stroke="#999999" strokeMiterlimit="10" />
        </g>
      </svg>

      {/* Middle: vertical body — height scales with capacity */}
      <svg
        width={DESIGN_WIDTH}
        height={midPx}
        viewBox={`0 ${TOP_END} ${DESIGN_WIDTH} ${MID_VB}`}
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <defs>
          <clipPath id={bandMidId}>
            <rect x="0" y={TOP_END} width={DESIGN_WIDTH} height={MID_VB} />
          </clipPath>
          <clipPath id={`${fillClipId}-mid`}>{interiorClipPaths}</clipPath>
        </defs>
        <g clipPath={`url(#${bandMidId})`}>
          <path d={PATH_BODY} fill="#E6E6E6" />
          {midFillStart < CONE_START ? (
            <g clipPath={`url(#${fillClipId}-mid)`}>
              <rect
                x="0"
                y={midFillStart}
                width={DESIGN_WIDTH}
                height={CONE_START - midFillStart}
                fill={fillColour}
              />
            </g>
          ) : null}
          <path d={PATH_BODY} stroke="#999999" strokeMiterlimit="10" />
          {PATH_LINES.map((d, i) => (
            <path key={i} d={d} stroke="#CFCFCF" strokeMiterlimit="10" />
          ))}
        </g>
      </svg>

      {/* Bottom cone — fixed height (capacity shown by body section only) */}
      <svg
        width={DESIGN_WIDTH}
        height={botPx}
        viewBox={`0 ${CONE_START} ${DESIGN_WIDTH} ${BOT_VB}`}
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <defs>
          <clipPath id={bandBotId}>
            <rect x="0" y={CONE_START} width={DESIGN_WIDTH} height={BOT_VB} />
          </clipPath>
          <clipPath id={`${fillClipId}-bot`}>{interiorClipPaths}</clipPath>
        </defs>
        <g clipPath={`url(#${bandBotId})`}>
          <path d={PATH_BODY} fill="#E6E6E6" />
          {botFillStart < DESIGN_BOTTOM ? (
            <g clipPath={`url(#${fillClipId}-bot)`}>
              <rect
                x="0"
                y={botFillStart}
                width={DESIGN_WIDTH}
                height={DESIGN_BOTTOM - botFillStart}
                fill={fillColour}
              />
            </g>
          ) : null}
          <path d={PATH_BODY} stroke="#999999" strokeMiterlimit="10" />
          <path d={PATH_SEPARATOR} stroke="#CFCFCF" strokeMiterlimit="10" />
        </g>
      </svg>
    </span>
  );
}

SingleBinIcon.propTypes = {
  className: PropTypes.string,
  fillPercent: PropTypes.number,
  fillColour: PropTypes.string,
  onClick: PropTypes.func,
  heightScale: PropTypes.number,
};
